import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Loader2, CheckCircle2, CircleSlash, XCircle } from "lucide-react";
import { getMyAds } from "@/lib/ads.functions";
import { adImageSrc } from "@/lib/image-src";
import {
  getPromotionStatus,
  promoteAd,
  listMyPromotions,
  cancelPromotion,
} from "@/lib/promotions.functions";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { myAdsQuery, myPromotionsQuery, myPromotionCampaignsQuery } from "@/lib/queries";
import {
  promotionDayOptions,
  maxPromotionDays,
  promotionCap,
  clampPromotionDays,
  promotionStepNote,
} from "@/lib/promotion-days";

export const Route = createFileRoute("/_authenticated/lyft")({
  validateSearch: (search: Record<string, unknown>): { slug?: string } =>
    typeof search.slug === "string" ? { slug: search.slug } : {},
  head: () => ({
    meta: [
      { title: "Lyft fram annonser — Sellavo" },
      {
        name: "description",
        content: "Välj vilken annons du vill lyfta fram, hur många dygn och följ dina kampanjer.",
      },
      { property: "og:title", content: "Lyft fram annonser — Sellavo" },
      {
        property: "og:description",
        content: "Välj annons, antal dygn och följ status på dina annonslyft.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(myAdsQuery()),
      context.queryClient.ensureQueryData(myPromotionsQuery()),
      context.queryClient.ensureQueryData(myPromotionCampaignsQuery()),
    ]);
  },
  component: PromotionsPage,
});

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

function PromotionsPage() {
  const queryClient = useQueryClient();
  const runPromote = useServerFn(promoteAd);

  const { slug: slugFromLink } = Route.useSearch();
  const [slug, setSlug] = useState<string>(slugFromLink ?? "");
  const [days, setDays] = useState<string>("");
  const [message, setMessage] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  const { data: ads } = useQuery(myAdsQuery());
  const { data: status } = useQuery(myPromotionsQuery());
  const { data: campaigns, isLoading } = useQuery(myPromotionCampaignsQuery());

  const step = status?.step ?? status?.days ?? 1;
  const remainingDays = status?.remaining ?? 0;
  const selectableAds = (ads ?? []).filter(
    (a: any) => !a.promoted_until || new Date(a.promoted_until).getTime() <= Date.now(),
  );
  // Ett lyft kan aldrig vara längre än annonsen visas.
  const chosenAd: any = (ads ?? []).find((a: any) => a.slug === slug);
  const adDaysLeft = chosenAd?.expires_at
    ? Math.floor((new Date(chosenAd.expires_at).getTime() - Date.now()) / 86400000)
    : null;
  const cap = promotionCap(remainingDays, adDaysLeft);
  // Lyft bokas i hela steg: 3, 6, 9 ... upp till det som finns kvar i månadskvoten.
  const dayOptions = useMemo(() => promotionDayOptions(step, cap), [cap, step]);
  const maxSelectableDays = maxPromotionDays(step, cap);
  const chosenDays = clampPromotionDays(step, cap, Number(days) || step);

  const promote = useMutation({
    mutationFn: () =>
      runPromote({
        data: { slug, days: clampPromotionDays(step, cap, Number(days) || step) },
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        setMessage({ tone: "warn", text: res.message });
        return;
      }
      setMessage({
        tone: "ok",
        text: `Annonsen är framlyft i ${res.days} dygn, till ${fmt(res.promotedUntil)}.`,
      });
      setSlug("");
      for (const key of [["my-ads"], ["my-promotions"], ["my-promotion-campaigns"], ["ads"]])
        queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: unknown) => {
      const text =
        err instanceof Error && err.message && !/\[object/.test(err.message)
          ? err.message
          : "Kunde inte lyfta annonsen just nu. Prova igen om en stund.";
      setMessage({ tone: "warn", text });
    },
  });

  const runCancel = useServerFn(cancelPromotion);
  const cancel = useMutation({
    mutationFn: (id: string) => runCancel({ data: { id } }),
    onSuccess: (res) => {
      setMessage({ tone: res.ok ? "ok" : "warn", text: res.message });
      for (const key of [["my-ads"], ["my-promotions"], ["my-promotion-campaigns"], ["ads"]])
        queryClient.invalidateQueries({ queryKey: key });
    },
    onError: () => setMessage({ tone: "warn", text: "Kunde inte avbryta kampanjen just nu." }),
  });

  const noPlan = status && status.limit === 0;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
        Lyft fram annonser
      </h1>
      <p className="mt-2 text-muted-foreground">
        Ett lyft placerar annonsen högst upp i listan. Välj annons och hur många dygn du vill
        använda.
      </p>

      {status && (
        <div className="mt-6 rounded-md border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            {noPlan ? (
              <span className="text-muted-foreground">
                Annonslyft ingår i Plus och Pro.{" "}
                <Link
                  to="/priser"
                  className="font-semibold text-primary underline underline-offset-2"
                >
                  Se planer
                </Link>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {status.unlimited ? (
                  <>
                    <strong className="text-foreground">Obegränsat</strong> med lyftdagar · steg om{" "}
                    {step} dygn per lyft
                  </>
                ) : (
                  <>
                    <strong className="text-foreground">{status.remaining}</strong> av{" "}
                    {status.limit} lyftdagar kvar den här månaden · steg om {step} dygn per lyft
                  </>
                )}
              </span>
            )}
          </div>
          {!noPlan && (
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${Math.round((status.remaining / Math.max(1, status.limit)) * 100)}%`,
                }}
              />
            </div>
          )}
        </div>
      )}

      {!noPlan && (
        <form
          className="mt-6 space-y-4 rounded-lg border border-border bg-card p-5"
          onSubmit={(e) => {
            e.preventDefault();
            setMessage(null);
            if (!slug)
              return setMessage({ tone: "warn", text: "Välj vilken annons du vill lyfta." });
            promote.mutate();
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="lyft-annons">Välj annons</Label>
              <Select value={slug} onValueChange={setSlug}>
                <SelectTrigger id="lyft-annons" aria-label="Välj annons att lyfta">
                  <SelectValue placeholder="Välj vilken annons som ska lyftas" />
                </SelectTrigger>
                <SelectContent>
                  {selectableAds.length === 0 ? (
                    <SelectItem value="__none" disabled>
                      Inga annonser att lyfta just nu
                    </SelectItem>
                  ) : (
                    selectableAds.map((a: any) => (
                      <SelectItem key={a.slug} value={a.slug}>
                        {a.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="lyft-dygn">Välj antal dygn</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger id="lyft-dygn" aria-label="Välj antal dygn för lyftet">
                  <SelectValue placeholder={`Välj antal dygn (${step}–${maxSelectableDays})`} />
                </SelectTrigger>
                <SelectContent>
                  {dayOptions.map((d: number) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} dygn{d === maxSelectableDays ? " (max)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {promotionStepNote(step)} Väljer du {chosenDays} dygn har du{" "}
                {Math.max(0, remainingDays - chosenDays)} lyftdagar kvar efteråt. Max{" "}
                {maxSelectableDays} dygn per lyft.
              </p>
            </div>
          </div>

          {message && (
            <p
              className={
                message.tone === "ok" ? "text-sm text-foreground" : "text-sm text-destructive"
              }
            >
              {message.text}
            </p>
          )}

          <Button type="submit" disabled={promote.isPending || dayOptions.length === 0}>
            {promote.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Lyft annonsen
          </Button>
        </form>
      )}

      <h2 className="mt-10 font-heading text-xl font-bold text-foreground">Mina kampanjer</h2>
      <div className="mt-3 space-y-3">
        {isLoading ? (
          <p className="text-muted-foreground">Laddar…</p>
        ) : (campaigns?.length ?? 0) === 0 ? (
          <p className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
            Du har inte lyft någon annons ännu.
          </p>
        ) : (
          campaigns!.map((c: any) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
            >
              {c.image ? (
                <img
                  src={adImageSrc(c.image)}
                  alt=""
                  loading="lazy"
                  className="h-14 w-14 shrink-0 rounded-lg object-cover"
                />
              ) : (
                <span className="h-14 w-14 shrink-0 rounded-lg bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">
                  {c.slug ? (
                    <Link to="/a/$slug" params={{ slug: c.slug }} className="hover:underline">
                      {c.title}
                    </Link>
                  ) : (
                    c.title
                  )}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {c.days} dygn · {fmt(c.startedAt)} – {fmt(c.endsAt)}
                  {c.active
                    ? ` · ${Math.max(1, Math.ceil((new Date(c.endsAt).getTime() - Date.now()) / 86_400_000))} ${Math.max(1, Math.ceil((new Date(c.endsAt).getTime() - Date.now()) / 86_400_000)) === 1 ? "dag" : "dagar"} kvar`
                    : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {c.active && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancel.isPending}
                    onClick={() => {
                      setMessage(null);
                      cancel.mutate(c.id);
                    }}
                  >
                    {cancel.isPending && cancel.variables === c.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    Avbryt
                  </Button>
                )}
                <span
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ${
                    c.active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {c.active ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : c.cancelled ? (
                    <XCircle className="h-3.5 w-3.5" />
                  ) : (
                    <CircleSlash className="h-3.5 w-3.5" />
                  )}
                  {c.active ? "Aktiv" : c.cancelled ? "Avbruten" : "Avslutad"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </main>
  );
}
